/**
 * ADD RESPONSIVE SEARCH ADS — /google-ads/<leads|traffic>/create/ad[?adgroup_id=…]
 *
 * One existing Search ad group, many ad drafts with a live Google-results
 * preview. The group's own keywords are loaded so the ad strength, the
 * preview and "Write with AI" all work from what the group actually targets.
 * Resumable: created ads are remembered by draft id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, Globe, Plus, Sparkles, Wand2 } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { googleAdsService, GoogleAdsAdGroupRow, SearchKeywordRow, SearchMatchType, SearchObjectiveKey } from '@/services/google-ads.service';
import { BackTo, ErrorBox, errMsg, Section, Spinner, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { isHttpUrl, Pub, uid } from '@/components/google-ads/objectives/objectiveShared';
import {
  ChipList, cloneRsa, KeywordDraft, newRsa, rsaIssues, SearchAdPreview, SearchGroupDraft, searchObjectiveOf, SearchObjectiveConfig,
} from './searchShared';
import { AdDraftCard } from './SearchDraftCards';
import { emptyAdsOf, useSearchAi } from './useSearchAi';

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);
const TARGET = 'target';
const fmtKeyword = (k: KeywordDraft) => (k.matchType === 'EXACT' ? `[${k.text}]` : k.matchType === 'PHRASE' ? `"${k.text}"` : k.text);

export default function SearchAdPage({ objective: cfg }: { objective: SearchObjectiveConfig }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);

  const [adgroups, setAdgroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [mineCampaigns, setMineCampaigns] = useState<Set<string>>(() => new Set());
  const [adgroupId, setAdgroupId] = useState(params.get('adgroup_id') || '');
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [existingAds, setExistingAds] = useState<number | null>(null);
  const [defaultUrl, setDefaultUrl] = useState('');
  const [takenLines, setTakenLines] = useState<string[]>([]);
  const adgroup = adgroups?.find((g) => g.id === adgroupId);

  // The ads live inside one pseudo-group carrying the real group's name and keywords, so the
  // shared AI actions, strength and preview work unchanged.
  const firstAd = useMemo(() => newRsa(1), []);
  const [groups, setGroups] = useState<SearchGroupDraft[]>([{ id: TARGET, name: '', cpcBid: '', defaultMatch: 'PHRASE', keywords: [], negatives: [], ads: [firstAd] }]);
  const target = groups[0];
  const ads = target.ads;
  const setAds = (next: typeof ads) => setGroups((gs) => [{ ...gs[0], ads: next }]);
  const [activeAd, setActiveAd] = useState(firstAd.id);

  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const madeRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!accountUid) return;
    Promise.all([
      googleAdsService.listAdGroups(accountUid),
      googleAdsService.listCampaigns(accountUid).catch(() => []),
      googleAdsService.search.campaignObjectives(accountUid).catch(() => ({} as Record<string, SearchObjectiveKey>)),
      googleAdsService.search.leadFormCampaigns(accountUid).catch(() => [] as string[]),
    ]).then(([groupsList, campaigns, tags, lf]) => {
      // A removed campaign keeps its ad groups ENABLED — Google refuses new ads in them.
      const removed = new Set(campaigns.filter((c) => c.status === 'REMOVED').map((c) => c.id));
      setAdgroups(groupsList.filter((g) => (!g.campaignChannelType || g.campaignChannelType === 'SEARCH') && g.status !== 'REMOVED' && !removed.has(g.campaignId)));
      const leadForms = new Set(lf);
      setMineCampaigns(new Set(campaigns.filter((c) => c.advertisingChannelType === 'SEARCH' && searchObjectiveOf(c, tags, leadForms) === cfg.key).map((c) => c.id)));
    }).catch(() => setAdgroups([]));
  }, [accountUid, cfg.key]);

  // Load the chosen group's keywords (for strength, preview and AI) and its landing page.
  useEffect(() => {
    setExistingAds(null);
    setTakenLines([]);
    if (!accountUid || !adgroupId) return;
    let alive = true;
    setKeywordsLoading(true);
    Promise.all([
      googleAdsService.search.adGroupKeywords(accountUid, adgroupId).then((r) => r.keywords).catch(() => [] as SearchKeywordRow[]),
      googleAdsService.listAds(accountUid, adgroupId).catch(() => []),
    ]).then(([kws, existing]) => {
      if (!alive) return;
      const keywords: KeywordDraft[] = kws.filter((k) => k.status !== 'REMOVED' && k.text)
        .map((k) => ({ id: uid(), text: k.text, matchType: (['BROAD', 'PHRASE', 'EXACT'].includes(k.matchType) ? k.matchType : 'BROAD') as SearchMatchType }));
      const live = existing.filter((a) => a.status !== 'REMOVED');
      setExistingAds(live.length);
      setTakenLines(live.flatMap((a) => a.headlines).filter(Boolean));
      const url = live.map((a) => a.finalUrls[0]).find((u) => u && isHttpUrl(u)) ?? '';
      setDefaultUrl(url);
      setGroups((gs) => [{ ...gs[0], keywords, ads: gs[0].ads.map((a) => (a.finalUrl || !url ? a : { ...a, finalUrl: url })) }]);
    }).finally(() => { if (alive) setKeywordsLoading(false); });
    return () => { alive = false; };
  }, [accountUid, adgroupId]);

  useEffect(() => {
    setGroups((gs) => (gs[0].name === (adgroup?.name ?? '') ? gs : [{ ...gs[0], name: adgroup?.name ?? '' }]));
  }, [adgroup?.name]);

  const ai = useSearchAi({ objective: cfg.key, setGroups, siteUrl: defaultUrl, avoidLines: takenLines });
  const emptyAds = useMemo(() => emptyAdsOf(groups, (id) => !!madeRef.current[id]), [groups, pub]); // eslint-disable-line react-hooks/exhaustive-deps

  const byCampaign = useMemo(() => {
    const m = new Map<string, { name: string; mine: boolean; groups: GoogleAdsAdGroupRow[] }>();
    for (const g of adgroups ?? []) {
      const row = m.get(g.campaignId) ?? { name: g.campaignName || `Campaign ${g.campaignId}`, mine: mineCampaigns.has(g.campaignId), groups: [] };
      row.groups.push(g);
      m.set(g.campaignId, row);
    }
    return Array.from(m.values()).sort((a, b) => Number(b.mine) - Number(a.mine) || a.name.localeCompare(b.name));
  }, [adgroups, mineCampaigns]);

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!adgroupId) i.push('Pick the ad group these ads belong to.');
    ads.forEach((a, k) => { const ai2 = rsaIssues(a); if (ai2.length) i.push(`${a.name.trim() || `Ad ${k + 1}`}: needs ${ai2.join(', ')}.`); });
    return i;
  }, [adgroupId, ads]);

  const submit = async () => {
    if (!accountUid || issues.length) return;
    setSaving(true); setError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    try {
      for (const a of ads) {
        if (madeRef.current[a.id]) continue;
        mark(a.id, 'running');
        try {
          const r = await googleAdsService.search.createAd(accountUid, {
            adGroupId: adgroupId, finalUrls: [a.finalUrl.trim()], headlines: filled(a.headlines), descriptions: filled(a.descriptions),
            path1: a.path1 || undefined, path2: a.path2 || undefined, status: 'PAUSED',
          });
          madeRef.current[a.id] = r.resourceName;
        } catch (e) { mark(a.id, 'failed'); setError(`${a.name.trim() || filled(a.headlines)[0] || 'Ad'}: ${errMsg(e)}`); return; }
        mark(a.id, 'done');
      }
      toast.success(`${ads.length === 1 ? 'Ad' : `${ads.length} ads`} created — Google reviews every ad; it stays paused until you enable it.`);
      navigate(cfg.base);
    } finally { setSaving(false); }
  };

  const active = ads.find((a) => a.id === activeAd) ?? ads[0] ?? null;

  return (
    <>
      <Topbar title="New search ads" breadcrumb={`Google Ads / ${cfg.title}`}
        description="Headlines and descriptions Google mixes per search — add as many ads as the group needs"
        back={<BackTo to={cfg.base} label={`Back to ${cfg.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && (
          <div className="flex min-h-[50vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>
        )}
        {auth !== undefined && !ready && (
          <EmptyState icon={<Globe size={36} className="text-text-muted" />} title="Connect a Google Ads account first"
            description="Authorise Google and choose a customer account, then come back."
            action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>} />
        )}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <div className="min-w-0 flex-1 space-y-4">
              <Card padded={false} className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2.5 text-xs text-text-muted">
                <span>Goal: <span className="font-medium text-text-primary">{cfg.goal}</span></span>
                <span>Type: <span className="font-medium text-text-primary">Responsive search ad</span></span>
                <span>Adding to: <span className="font-medium text-text-primary">{adgroup ? `${adgroup.name}${adgroup.campaignName ? ` · ${adgroup.campaignName}` : ''}` : 'pick an ad group'}</span></span>
                <Tag>starts paused</Tag>
              </Card>

              <Section title="Ad group" hint="The ads show for this group's keywords.">
                <Select value={adgroupId} onChange={(e) => setAdgroupId(e.target.value)} disabled={Object.keys(madeRef.current).length > 0}>
                  <option value="">{adgroups === null ? 'Loading ad groups…' : 'Pick an ad group'}</option>
                  {byCampaign.map((c) => (
                    <optgroup key={c.name} label={c.mine ? c.name : `${c.name} (other Search)`}>
                      {c.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </optgroup>
                  ))}
                </Select>
                {adgroupId && (
                  <div className="mt-3">
                    <div className="mb-1.5 flex items-baseline justify-between">
                      <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Keywords in this group</span>
                      {existingAds !== null && <span className="text-2xs text-text-muted">{existingAds} ad{existingAds === 1 ? '' : 's'} already</span>}
                    </div>
                    {keywordsLoading ? <span className="flex items-center gap-1.5 text-2xs text-text-muted"><Spinner size={11} /> Loading…</span>
                      : <ChipList items={target.keywords.map(fmtKeyword)} empty="No keywords — add some to the group so these ads can show" max={16} />}
                  </div>
                )}
              </Section>

              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/30 bg-primary-soft/40 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"><Sparkles size={16} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-text-primary">Write the ads with AI</div>
                  <div className="text-2xs text-text-muted">Grounded in each ad's landing page and this group's keywords. Every line stays editable.</div>
                </div>
                <Button size="sm" variant={emptyAds.length ? 'primary' : 'secondary'} iconLeft={<Wand2 size={13} />} loading={ai.busy === 'all'}
                  disabled={!!ai.busy || emptyAds.length === 0 || !adgroupId} onClick={() => ai.writeAll(emptyAds)}>
                  {emptyAds.length ? `Write ${emptyAds.length} empty ad${emptyAds.length === 1 ? '' : 's'}` : 'All ads have copy'}
                </Button>
              </div>

              {ads.map((a, k) => (
                <AdDraftCard key={a.id} ad={a} index={k} keywords={target.keywords} focused={active?.id === a.id} onFocus={() => setActiveAd(a.id)}
                  onChange={(p) => { setAds(ads.map((x) => (x.id === a.id ? { ...x, ...p } : x))); if (p.headlines || p.descriptions) ai.copyEdited(a.id); }}
                  made={!!madeRef.current[a.id]} state={pub[a.id]} disabled={saving}
                  onDuplicate={() => { const c = cloneRsa(a); setAds([...ads.slice(0, k + 1), c, ...ads.slice(k + 1)]); setActiveAd(c.id); }}
                  onRemove={ads.length > 1 ? () => setAds(ads.filter((x) => x.id !== a.id)) : undefined}
                  onWrite={() => ai.write(target, a)} writing={ai.busy === `ad:${a.id}` || (ai.busy === 'all' && emptyAds.some((x) => x.a.id === a.id))}
                  aiFilled={ai.aiAds.has(a.id)} />
              ))}

              <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
                onClick={() => { const a = newRsa(ads.length + 1, ads[ads.length - 1]?.finalUrl || defaultUrl); setAds([...ads, a]); setActiveAd(a.id); }}>
                Add another ad
              </Button>

              {issues.length > 0 && !error && (
                <ul className="space-y-1 rounded-xl border border-warning/30 bg-warning-soft/40 px-4 py-3">
                  {issues.slice(0, 6).map((i) => <li key={i} className="text-2xs text-warning">{i}</li>)}
                </ul>
              )}
              {error && (
                <div>
                  <ErrorBox message={error} />
                  <p className="mt-1 text-2xs text-text-muted">Fix it and try again — ads already created are kept, not repeated.</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" onClick={() => navigate(cfg.base)} disabled={saving}>Cancel</Button>
                <Button onClick={submit} loading={saving} disabled={saving || issues.length > 0} iconLeft={<Check size={15} />}>
                  {error ? 'Retry' : ads.length === 1 ? 'Create ad' : `Create ${ads.length} ads`}
                </Button>
              </div>
            </div>

            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24"><SearchAdPreview ad={active} keywords={target.keywords} /></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
